<script setup>
import { computed, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import CsvImportDialog from 'dashboard/components-next/arrowsAi/CsvImportDialog.vue';

const props = defineProps({
  exampleFilename: { type: String, required: true },
  exampleContent: { type: String, required: true },
  isImporting: { type: Boolean, default: false },
  spreadsheet: { type: Boolean, default: false },
});

const emit = defineEmits(['import', 'export', 'exportExcel']);
const { t } = useI18n();
const open = ref(false);
const dialogRef = ref(null);

const menuItems = computed(() => {
  const items = [
    {
      label: t('ARROWS_AI.CSV.IMPORT'),
      action: 'import',
      icon: 'i-lucide-upload',
    },
    {
      label: t('ARROWS_AI.CSV.EXPORT'),
      action: 'export',
      icon: 'i-lucide-download',
    },
  ];
  if (props.spreadsheet) {
    items.push({
      label: t('ARROWS_AI.CSV.EXPORT_EXCEL'),
      action: 'exportExcel',
      icon: 'i-lucide-file-spreadsheet',
    });
  }
  return items;
});

const onAction = item => {
  open.value = false;
  if (item.action === 'export') {
    emit('export');
    return;
  }
  if (item.action === 'exportExcel') {
    emit('exportExcel');
    return;
  }
  dialogRef.value?.open();
};

const onImport = file => emit('import', file);

defineExpose({
  closeImport: () => dialogRef.value?.close(),
});
</script>

<template>
  <OnClickOutside @trigger="open = false">
    <div class="relative">
      <Button
        icon="i-lucide-ellipsis-vertical"
        variant="faded"
        color="slate"
        size="sm"
        :title="t('ARROWS_AI.CSV.MENU')"
        @click="open = !open"
      />
      <DropdownMenu
        v-if="open"
        :menu-items="menuItems"
        class="absolute top-10 ltr:right-0 rtl:left-0 z-50"
        @action="onAction"
      />
    </div>
  </OnClickOutside>
  <CsvImportDialog
    ref="dialogRef"
    :example-filename="exampleFilename"
    :example-content="exampleContent"
    :is-importing="isImporting"
    :accept="
      spreadsheet
        ? '.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : '.csv,text/csv'
    "
    :title="spreadsheet ? t('ARROWS_AI.CSV.SPREADSHEET_TITLE') : ''"
    :description="spreadsheet ? t('ARROWS_AI.CSV.SPREADSHEET_DESCRIPTION') : ''"
    :choose-label="spreadsheet ? t('ARROWS_AI.CSV.CHOOSE_SPREADSHEET') : ''"
    @import="onImport"
  />
</template>
